import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import type { KanzleilandIntegration } from "@/apis/gql/types";
import { Alert, AlertTitle } from "@/components/ui/alert";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { TypographyLarge, TypographySmall } from "@/components/ui/typography";
import { CircleAlert } from "lucide-react";

import { DisconnectButton } from "./disconnect-button";
import { DocumentImportHistory } from "./document-import-history";
import { KanzleilandIntegrationStepOne } from "./steps/step-one";
import { KanzleilandIntegrationStepThree } from "./steps/step-three";
import { KanzleilandIntegrationStepTwo } from "./steps/step-two";

type Props = {
  integration: KanzleilandIntegration;
  loading: boolean;
  onCreate: (
    integrationAuth: KanzleilandIntegration["integrationAuth"],
  ) => Promise<void>;
  onDisconnect: () => Promise<void>;
  onFolderSelectionChange: (
    selectedFolders: KanzleilandIntegration["integrationConfig"]["folderIds"],
  ) => Promise<void>;
  onImportDateChange: (
    importDate: KanzleilandIntegration["integrationConfig"]["importDate"],
  ) => Promise<void>;
  folders?: any;
};

export const KanzleilandIntegrations = ({
  integration,
  onCreate,
  onDisconnect,
  onFolderSelectionChange,
  onImportDateChange,
  folders,
}: Props) => {
  const { t } = useTranslation();

  const isConnected = useMemo(() => {
    if (!integration) return false;
    return (
      integration.integrationAuth.userToken &&
      integration.integrationAuth.targetToken
    );
  }, [integration]);

  const showStepTwo = useMemo(() => {
    return isConnected;
  }, [isConnected]);

  const showStepThree = useMemo(() => {
    return isConnected && integration?.integrationConfig?.importDate;
  }, [isConnected, integration?.integrationConfig?.importDate]);

  return (
    <Card>
      <CardHeader>
        <div className="flex justify-between">
          <div className="flex items-start gap-4">
            <div className="space-y-2">
              <TypographyLarge>
                {t("integrations.kanzleiland.title")}
              </TypographyLarge>
              <TypographySmall className="text-muted-foreground">
                {t("integrations.kanzleiland.description")}
              </TypographySmall>
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent className="flex flex-col gap-4 px-4 pb-4">
        <Alert className="mb-3 p-4" variant="warning">
          <AlertTitle className="font-bold flex items-center">
            <CircleAlert size={16} className="mr-2" />
            {t("integrations.kanzleiland.first_client_alert.title")}
          </AlertTitle>
          <div className="p-0 m-0">
            {t("integrations.kanzleiland.first_client_alert.description")}
          </div>
        </Alert>
        <KanzleilandIntegrationStepOne
          onSubmit={onCreate}
          userToken={integration?.integrationAuth.userToken}
          targetToken={integration?.integrationAuth.targetToken}
        />
        <KanzleilandIntegrationStepTwo
          disabled={!showStepTwo}
          importDate={integration?.integrationConfig?.importDate}
          onSubmit={onImportDateChange}
        />
        <KanzleilandIntegrationStepThree
          disabled={!showStepThree}
          folders={folders}
          selectedFolders={integration?.integrationConfig?.folderIds ?? []}
          onSelectionChange={onFolderSelectionChange}
        />
        {isConnected && (
          <div className="flex justify-end">
            <DisconnectButton onDisconnect={onDisconnect} />
          </div>
        )}
        {isConnected && <DocumentImportHistory />}
      </CardContent>
    </Card>
  );
};

//18af892fb9cd9a7f59ade574125a2acabb8a70e15df7396e03f882ea3000ae4281e84174146747fe948dcb59a88dc4f2d3eb303cdff867a343bd34064c0218478e
//19fed4e9a9271456f88440f02f873982387f71b018372a652ee3dd3df097d98a93010f420bb421c387a9dae993e90aa872c090754b839d88f04385ad27469a76bb
